import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import ServicesPage from "@/pages/ServicesPage";
import ProcessPage from "@/pages/ProcessPage";
import RealisationsPage from "@/pages/RealisationsPage";
import ConseilsPage from "@/pages/ConseilsPage";
import ArticlePage from "@/pages/ArticlePage";
import NousRejoindrePage from "@/pages/NousRejoindrePage";
import DevenirPartenairePage from "@/pages/DevenirPartenairePage";
import PartnerApplicationPage from "@/pages/PartnerApplicationPage";
import CompanyApplicationPage from "@/pages/CompanyApplicationPage";
import PartnerPortalPage from "@/pages/PartnerPortalPage";
import AboutPage from "@/pages/AboutPage";
import FaqPage from "@/pages/FaqPage";
import ModelsPage from "@/pages/ModelsPage";
import TarifsPage from "@/pages/TarifsPage";
import MentionsLegalesPage from "@/pages/MentionsLegalesPage";
import PolitiqueConfidentialitePage from "@/pages/PolitiqueConfidentialitePage";
import CookiesPage from "@/pages/CookiesPage";
import NotFoundPage from "@/pages/NotFoundPage";
import HouseModelPage from "@/pages/HouseModelPage";
import EstimerProjet from "@/pages/EstimerProjet";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import SuiviChantier from "@/pages/SuiviChantier";
import PartnerLayout from "@/partner/PartnerLayout";
import PartnerDashboard from "@/partner/pages/PartnerDashboard";
import PartnerProspects from "@/partner/pages/PartnerProspects";
import PartnerClients from "@/partner/pages/PartnerClients";
import PartnerProjects from "@/partner/pages/PartnerProjects";
import PartnerProjectDetail from "@/partner/pages/PartnerProjectDetail";
import PartnerSmallWorks from "@/partner/pages/PartnerSmallWorks";
import PartnerPros from "@/partner/pages/PartnerPros";
import PartnerServices from "@/partner/pages/PartnerServices";
import PartnerCommunication from "@/partner/pages/PartnerCommunication";
import PartnerCommissions from "@/partner/pages/PartnerCommissions";
import PartnerCompany from "@/partner/pages/PartnerCompany";
import PartnerAgenda from "@/partner/pages/PartnerAgenda";
import { EngineProvider } from "@/engine/store";
import EntrepriseSpace from "@/entreprise/EntrepriseSpace";
import ClientProjectPage from "@/client/ClientProjectPage";
import ClientHomePage from "@/client/ClientHomePage";
import ClientSmallWorkPage from "@/client/ClientSmallWorkPage";
import Phase3AccessPage from "@/pages/Phase3AccessPage";
import { Phase3Gate } from "@/components/Phase3Gate";
import PartnerTeam from "@/partner/pages/PartnerTeam";
import { PartnerPermissionGate } from "@/partner/PartnerPermissionGate";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";

function App() {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
      <EngineProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/comment-ca-marche" element={<ProcessPage />} />
          <Route path="/realisations" element={<RealisationsPage />} />
          <Route path="/conseils" element={<ConseilsPage />} />
          <Route path="/conseils/:slug" element={<ArticlePage />} />
          <Route path="/nous-rejoindre" element={<NousRejoindrePage />} />
          <Route path="/devenir-partenaire" element={<DevenirPartenairePage />} />
          <Route path="/nous-rejoindre/partenaire" element={<PartnerApplicationPage />} />
          <Route path="/nous-rejoindre/entreprise" element={<CompanyApplicationPage />} />
          <Route path="/partenaire/completer-dossier/:token" element={<PartnerPortalPage />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/modeles" element={<ModelsPage />} />
          <Route path="/tarifs" element={<TarifsPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/modeles/:slug" element={<HouseModelPage />} />
          <Route path="/estimer" element={<EstimerProjet />} />
          <Route path="/suivi" element={<SuiviChantier />} />
          <Route path="/suivi/:code" element={<SuiviChantier />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/centrale" element={<AdminDashboard />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/acces-phase3" element={<Phase3AccessPage />} />
          <Route path="/partenaire" element={<Phase3Gate role="PARTNER"><PartnerLayout /></Phase3Gate>}>
            <Route index element={<PartnerDashboard />} />
            <Route path="agenda" element={<PartnerAgenda />} />
            <Route path="prospects" element={<PartnerPermissionGate any={["prospects.view_all", "prospects.view_assigned"]}><PartnerProspects /></PartnerPermissionGate>} />
            <Route path="clients" element={<PartnerPermissionGate any={["clients.view_all", "clients.view_assigned"]}><PartnerClients /></PartnerPermissionGate>} />
            <Route path="projets" element={<PartnerPermissionGate any={["projects.view_all", "projects.view_assigned"]}><PartnerProjects /></PartnerPermissionGate>} />
            <Route path="projets/:ref" element={<PartnerPermissionGate any={["projects.view_all", "projects.view_assigned"]}><PartnerProjectDetail /></PartnerPermissionGate>} />
            <Route path="petits-travaux" element={<PartnerPermissionGate ownerOnly><PartnerSmallWorks /></PartnerPermissionGate>} />
            <Route path="professionnels" element={<PartnerPermissionGate any={["site.view"]}><PartnerPros /></PartnerPermissionGate>} />
            <Route path="services" element={<PartnerPermissionGate any={["central_orders.view", "central_orders.create"]}><PartnerServices /></PartnerPermissionGate>} />
            <Route path="communication" element={<PartnerPermissionGate ownerOnly><PartnerCommunication /></PartnerPermissionGate>} />
            <Route path="commissions" element={<PartnerPermissionGate any={["finance.view_global_revenue"]}><PartnerCommissions /></PartnerPermissionGate>} />
            <Route path="entreprise" element={<PartnerCompany />} />
            <Route path="entreprise/equipe" element={<PartnerPermissionGate any={["team.view"]}><PartnerTeam /></PartnerPermissionGate>} />
          </Route>
          <Route path="/entreprise/*" element={<Phase3Gate role="COMPANY"><EntrepriseSpace /></Phase3Gate>} />
          <Route path="/espace-client" element={<Phase3Gate role="CLIENT"><ClientHomePage /></Phase3Gate>} />
          <Route path="/espace-client/petits-travaux/:ref" element={<Phase3Gate role="CLIENT"><ClientSmallWorkPage /></Phase3Gate>} />
          <Route path="/espace-client/:ref" element={<Phase3Gate role="CLIENT"><ClientProjectPage /></Phase3Gate>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster position="top-center" richColors />
        <CookieConsentBanner />
      </EngineProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
